'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarIcon } from 'lucide-react';
import { tr, enUS, de, ro, ru, sr, zhCN, type Locale } from 'date-fns/locale';

import { Calendar } from './calendar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Button } from './button';
import { cn } from './lib/utils';

// ---------------------------------------------------------------------------
// DateTimeField — locale-aware date+time picker, ported from the
// site-builder blocks package's `SbDateTimeInput`
// (`_shared/SbDateTimeInput.tsx`) onto this package's `Popover` + `Calendar`
// primitives. Replaces the native `<input type="datetime-local">`, whose
// popup calendar ignores the page's locale and can't be themed.
// ---------------------------------------------------------------------------

export interface DateTimeFieldProps {
  /** ISO datetime string, or '' when unset. */
  value: string;
  onChange: (iso: string) => void;
  /** BCP-47 locale driving month/weekday names, digit order and separators. */
  locale: string;
  id?: string;
  disabled?: boolean;
  clearLabel?: string;
  todayLabel?: string;
  placeholder?: string;
  'aria-required'?: 'true' | 'false';
  className?: string;
}

const DATE_FNS_LOCALES: Record<string, Locale> = {
  tr,
  en: enUS,
  de,
  ro,
  ru,
  sr,
  zh: zhCN,
};

function resolveDateFnsLocale(bcp47: string): Locale {
  const base = bcp47.split('-')[0]?.toLowerCase() ?? '';
  return DATE_FNS_LOCALES[base] ?? enUS;
}

function formatValue(d: Date, locale: string): string {
  try {
    const date = new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(d);
    const time = new Intl.DateTimeFormat(locale, {
      hour: '2-digit',
      minute: '2-digit',
    }).format(d);
    return `${date}, ${time}`;
  } catch {
    return d.toString();
  }
}

/** `HH:mm` for the time input, in local time (not UTC). */
function timeInputValue(d: Date): string {
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

export function DateTimeField({
  value,
  onChange,
  locale,
  id,
  disabled = false,
  clearLabel,
  todayLabel,
  placeholder,
  className,
  ...aria
}: DateTimeFieldProps) {
  const { t } = useTranslation('ui');
  const [open, setOpen] = React.useState(false);
  const dateFnsLocale = React.useMemo(() => resolveDateFnsLocale(locale), [locale]);
  const parsed = value ? new Date(value) : undefined;
  const valid = parsed && !Number.isNaN(parsed.getTime());

  const commit = (next: Date) => {
    onChange(next.toISOString());
  };

  const handleDaySelect = (day: Date | undefined) => {
    if (!day) return;
    const next = new Date(day);
    if (valid) {
      next.setHours(parsed.getHours(), parsed.getMinutes(), 0, 0);
    }
    commit(next);
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const [h, m] = e.target.value.split(':').map(Number);
    if (h === undefined || m === undefined) return;
    const base = valid ? new Date(parsed) : new Date();
    base.setHours(h, m, 0, 0);
    commit(base);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          aria-required={aria['aria-required']}
          className={cn('w-full justify-start gap-2 font-normal', className)}
        >
          <CalendarIcon className="size-4 shrink-0 text-muted-foreground" />
          <span className={cn('truncate', !valid && 'text-muted-foreground')}>
            {valid ? formatValue(parsed, locale) : (placeholder ?? t('dateTime.placeholder'))}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={valid ? parsed : undefined}
          onSelect={handleDaySelect}
          locale={dateFnsLocale}
          autoFocus
        />
        <div className="flex items-center gap-2 border-t p-3">
          <input
            type="time"
            value={valid ? timeInputValue(parsed) : ''}
            onChange={handleTimeChange}
            disabled={!valid}
            className="h-9 flex-1 rounded-md border border-input bg-transparent px-2.5 text-sm outline-none disabled:opacity-50"
          />
        </div>
        <div className="flex items-center justify-between gap-2 border-t p-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onChange('')}
            disabled={!valid}
          >
            {clearLabel ?? t('common:actions.clear')}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              commit(new Date());
            }}
          >
            {todayLabel ?? t('dateTime.today')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
